import {
    ArrayTemplate,
    downloadWorkbook,
    DynamicField,
    exportExcelFromWorkBook,
    getRecordRowCountForSheet,
    QuantityField,
    RequiredField,
    SheetTemplate,
    WorkBookTemplate,
    writeExcelFromWorkBook
} from "../../../../common/upload/excel.utils";
import {SourcesProvider} from "../../../../utils/source-providers/sources-provider";
import {
    AggregatedDataPoint,
    ConnectionType,
    DataType,
    GetMeasurementsByConnection,
    GetMeasurementsByMeter,
    MeasurementsResult,
    MeterType,
    TimeRange,
    TimeResolution
} from "@flowmaps/flowmaps-typescriptmodels";
import {RefdataUtils} from "../../refdata-utils";
import lodash, {cloneDeep} from "lodash";
import {Slot} from "../../../../utils/chart-data-provider";
import {DashboardContext} from "../../../dashboard/dashboard.context";
import {AppContext} from "../../../../app-context";
import {mergeMap, tap} from "rxjs/operators";
import * as XLSX from "xlsx-js-style";
import moment from "moment";
import {removeItem} from "../../../../common/utils";
import {combineLatest, Observable} from 'rxjs';
import {sendQuery} from "../../../../common/app-common-utils";
import {Entity, EntityType} from "../../../../handlers/entity";
import {MeterViewEntity} from "../../../../handlers/meter-views-standalone-handler";
import {Measurements} from "../../../measurements-component/measurements";

export function downloadBulkMonthData(sourceProvider: SourcesProvider<any>, timeRange: TimeRange, aggregated?: boolean) {
    const info = prepareDownload(timeRange, bulkDataDownloadTemplate);
    combineLatest([sendQuery("getConnectionsAsEntities"), sendQuery("getMeterViews"), getMeasurements(timeRange, sourceProvider, !aggregated)])
        .subscribe(([entities, allMeterViews, data]) => {
            const meterViews = allMeterViews.filter(m => m.meterView.meterType != MeterType.INTERMEDIATE)
            const records = {
                data: getConnectionData(timeRange, data, entities, meterViews, sourceProvider, info.slots, info.yearSlots, info.dynamicColumns, aggregated)
            };
            const rowCount = getRecordRowCountForSheet(info.template.sheets[0].template, records);
            exportExcelFromWorkBook(downloadWorkbook("/assets/templates/bulk-data-download-new.xlsx")
                .pipe(tap(wb => {
                    const sheet = wb.Sheets["Data"];
                    const sheetTemplate = info.template.sheets.find(s => s.name === "Data");
                    addDynamicColumns(sheet, sheetTemplate, info.dynamicColumns, rowCount);
                })), info.template, records, "bulk-data-export.xlsx");
        });
}

export function downloadSubmeterData(sourceProvider: SourcesProvider<any>, timeRange: TimeRange) {
    const info = prepareDownload(timeRange, submetersDataDownloadTemplate);
    combineLatest([sendQuery("getMetersAsEntities"), sendQuery("getMeterViews"), getSubmeterMeasurements(timeRange, sourceProvider)])
        .subscribe(([entities, allMeterViews, data]) => {
            const { otherData, chargePointData } = getSubmeterData(timeRange, data, entities, allMeterViews, sourceProvider, info.slots, info.yearSlots, info.dynamicColumns);
            const records = {
                "data": otherData,
                "chargePoints": chargePointData
            };
            const rowCountData = getRecordRowCountForSheet(info.template.sheets.find(s => s.name === "Data").template, { data: otherData });
            const rowCountChargePoints = getRecordRowCountForSheet(info.template.sheets.find(s => s.name === "ChargePoints").template, { chargePoints: chargePointData });
            exportExcelFromWorkBook(downloadWorkbook("/assets/templates/submeters-download-data.xlsx")
                    .pipe(tap(wb => {
                        const dataSheet = wb.Sheets["Data"];
                        const dataTemplate = info.template.sheets.find(s => s.name === "Data");
                        addDynamicColumns(dataSheet, dataTemplate, info.dynamicColumns, rowCountData);

                        const cpDataSheet = wb.Sheets["ChargePoints"];
                        const cpTemplate = info.template.sheets.find(s => s.name === "ChargePoints");
                        addDynamicColumns(cpDataSheet, cpTemplate, info.dynamicColumns, rowCountChargePoints);
                    })),
                info.template, records, "bulk-data-export.xlsx"
            );
        });
}

export function writeBulkMonthDataAsExcel(sourceProvider: SourcesProvider<any>, timeRange: TimeRange): Observable<any> {
    const info = prepareDownload(timeRange, bulkDataDownloadTemplate);
    return combineLatest([sendQuery("getConnectionsAsEntities"), sendQuery("getMeterViews"), getMeasurements(timeRange, sourceProvider, false)])
        .pipe(mergeMap(([connections, allMeterViews, data]) => {
            const meterViews = allMeterViews.filter(m => m.meterView.meterType != MeterType.INTERMEDIATE)
            const records = {
                data: getConnectionData(timeRange, data, connections, meterViews, sourceProvider, info.slots, info.yearSlots, info.dynamicColumns)
            };
            const rowCount = getRecordRowCountForSheet(info.template.sheets[0].template, records);
            return writeExcelFromWorkBook(downloadWorkbook("/assets/templates/bulk-data-download.xlsx")
                    .pipe(tap(wb => addDynamicColumns(wb.Sheets["Data"], info.template.sheets.find(
                        s => s.name === "Data"), info.dynamicColumns, rowCount))),
                info.template, records, {
                    bookType: "xlsx",
                    type: "base64"
                });
        }));
}

function prepareDownload(timeRange: TimeRange, temp: WorkBookTemplate): BulkDownloadInfo {
    const slots = Measurements.getSlots({
        ...timeRange,
        resolution: TimeResolution.month
    });
    const endDate = moment(timeRange.end);
    const yearSlots = Measurements.getSlots({
        start: moment(timeRange.start).startOf("year").toISOString(),
        end: endDate.clone().startOf('year').isSame(endDate) ? endDate.clone().toISOString()
            : endDate.clone().add(1, 'year').startOf("year").toISOString(),
        resolution: TimeResolution.year
    });
    const template = cloneDeep(temp);
    return { slots, yearSlots, dynamicColumns: getDynamicColumns(template, slots, yearSlots), template };
}

function getMeasurements(timeRange: TimeRange, sourceProvider: SourcesProvider<any>, addPower: boolean): Observable<MeasurementsResult[]> {
    return sendQuery("com.flowmaps.api.measurements.GetMeasurementsByConnection", <GetMeasurementsByConnection>{
        resolution: TimeResolution.month,
        timeRange: AppContext.timeRangeToQuery(timeRange),
        sources: sourceProvider.sourceSelectionAfterCleanup(),
        unrounded: true,
        addPower: addPower,
    }, {caching: false, showSpinner: true});
}

function getSubmeterMeasurements(timeRange: TimeRange, sourceProvider: SourcesProvider<any>): Observable<MeasurementsResult[]> {
    const locationIds = sourceProvider.getTreeViewSelection(sourceProvider.sourceSelectionAfterCleanup()).flatMap(s => {
        const location = s.info.type === "location" ? [s] : [];
        const subLocations = s.subItems?.filter(i => i.info.type === "location") ?? [];
        return [...location, ...subLocations];
    }).map(item => item.info.id);

    return sendQuery("com.flowmaps.api.measurements.GetMeasurementsByMeter", <GetMeasurementsByMeter>{
        locationIds: locationIds,
        resolution: TimeResolution.month,
        timeRange: AppContext.timeRangeToQuery(timeRange),
        topLevelOnly: false,
        unrounded: true,
        addPower: true
    }, {caching: false, showSpinner: true});
}


function aggregate(measurements: { [P in DataType]?: AggregatedDataPoint[] }) {
    let data = lodash.cloneDeep(measurements || {});
    for (const [targetType, sources] of dataTypesMapped) {
        const allPoints: AggregatedDataPoint[] = sources.flatMap(dt => data[dt as DataType] || []);
        if (allPoints.length > 0) {
            const grouped = lodash.groupBy(allPoints, dp => dp.timeRange.start);
            data[targetType as DataType] = Object.entries(grouped).map(([_, points]) => ({
                timeRange: points[0].timeRange,
                value: lodash.sumBy(points, p => p.value)
            }));
            sources.forEach(dt => {
                if (dt !== targetType) delete data[dt as DataType];
            });
        }
    }
    return data;
}

function getConnectionData(timeRange: TimeRange, data: MeasurementsResult[], entities: Entity[], meterViews: MeterViewEntity[],
                           sourceProvider: SourcesProvider<any>, slots: Slot[], yearSlots: Slot[], dynamicColumns: DynamicColumn[], aggregated: boolean = false) {
    const allFilteredConnections = sourceProvider.getAllSourcesByType(EntityType.connection)
        .filter(s => s.connectionType !== ConnectionType.ChargePoint &&  !s.source.connection.meters.every(m => m.info.type === MeterType.INTERMEDIATE))
        .map(s => s.source.connection);
    const connectionIdsWithoutData = allFilteredConnections.map(c => c.connectionId);
    const connectionsWithData = data.flatMap(m => {
        const entity = entities.find(c => c.connection.connectionId === m.entityId
            && c.connection.info.connectionType != ConnectionType.ChargePoint
            && !c.connection.meters.every(m => m.info.type === MeterType.INTERMEDIATE));
        if (!entity) return [];
        removeItem(connectionIdsWithoutData, entity.connection.connectionId);
        let measurements = aggregated ? aggregate(m.measurements) :lodash.cloneDeep(m.measurements || {});
        return Object.entries(measurements)
            .filter(e => getAllowedMeasurementTypes(false, !aggregated).includes(e[0] as DataType))
            .map(e => {
                const data = {
                    organisation: RefdataUtils.organisationInfoFormatter(entity.organisation.info),
                    location: RefdataUtils.locationInfoFormatter(entity.location.info),
                    buildingCode: entity.location.info.buildingCode,
                    ean: entity.connection.info.code,
                    surveyor: entity.connection.info.surveyor.name,
                    marketSegment: entity.connection.info.marketSegment,
                    connectionType: entity.connection.info.connectionType,
                    measurementType: aggregated ? AppContext.entityPerformanceMeasurementName(e[0] as DataType) : AppContext.measurementName(e[0] as DataType),
                    type: e[0] as DataType,
                    measurementUnit: DashboardContext.getMeasurementUnit(e[0] as DataType, false),
                    completeness: AppContext.computeCompleteness(meterViews.filter(
                        v => v.meterView.connectionId === entity.connection.connectionId), timeRange, [])
                };
                const slotsWithData = Measurements.appendAggregatedDataToSlots(e[1], slots)
                    .concat(Measurements.appendAggregatedDataToSlots(e[1], yearSlots));
                dynamicColumns.forEach(d => {
                    const slot = slotsWithData.find(s => s.label === d.fieldName);
                    if (slot) {
                        data[d.fieldName] = slot.values.length > 0 ? lodash.sum(slot.values) : null;
                    }
                });
                return data;
            });
    });
    const connectionsWithoutData = connectionIdsWithoutData.map(c => {
        const entity = entities.find(con => con.connection.connectionId === c);
        return entity ? <any>{
            organisation: RefdataUtils.organisationInfoFormatter(entity.organisation.info),
            location: RefdataUtils.locationInfoFormatter(entity.location.info),
            buildingCode: entity.location.info.buildingCode,
            ean: entity.connection.info.code,
            surveyor: entity.connection.info.surveyor.name,
            marketSegment: entity.connection.info.marketSegment,
            connectionType: entity.connection.info.connectionType
        } : null;
    });
    return sortRecords(connectionsWithData).concat(connectionsWithoutData);
}

function getSubmeterData(timeRange: TimeRange, data: MeasurementsResult[], entities: Entity[], meterViews: MeterViewEntity[],
                         sourceProvider: SourcesProvider<any>, slots: Slot[], yearSlots: Slot[], dynamicColumns: DynamicColumn[]) {
    const allMeters = sourceProvider.getAllSourcesByType(EntityType.meter).map(s => s.source.meter);
    const metersIdsWithoutData = allMeters.map(m => m.meterId);
    const metersWithData = data.flatMap(m => {
        const entity = entities.find(e => e.meter.meterId === m.entityId || (e.connection.connectionId === m.entityId && e.meter.info.type === MeterType.PRIMARY));
        if (entity) {
            removeItem(metersIdsWithoutData, entity.meter.meterId);
            let measurements = aggregate(m.measurements);
            return Object.entries(measurements)
                .filter(e => getAllowedMeasurementTypes(true, true).includes(e[0] as DataType))
                .map(e => {
                    const data = {
                        organisation: RefdataUtils.organisationInfoFormatter(entity.organisation.info),
                        location: RefdataUtils.locationInfoFormatter(entity.location.info),
                        buildingCode: entity.location.info.buildingCode,
                        ean: entity.connection.info.code,
                        marketSegment: entity.connection.info.marketSegment,
                        connectionType: entity.connection.info.connectionType,
                        measurementType: AppContext.entityPerformanceMeasurementName(e[0] as DataType),
                        type: e[0] as DataType,
                        label: (entity.meter.info.label || entity.meter.details?.label) ?? "",
                        meterType: entity.meter.info.type ?? "",
                        measurementUnit: DashboardContext.getMeasurementUnit(e[0] as DataType, false),
                        completeness: AppContext.computeCompleteness(meterViews.filter(
                            v => v.meterView.connectionId === entity.connection.connectionId), timeRange, [])
                    };
                    const slotsWithData = Measurements.appendAggregatedDataToSlots(e[1], slots).concat(Measurements.appendAggregatedDataToSlots(e[1], yearSlots));
                    dynamicColumns.forEach(d => {
                        const slot = slotsWithData.find(s => s.label === d.fieldName);
                        if (slot) data[d.fieldName] = slot.values.length > 0 ? lodash.sum(slot.values) : null;
                    });
                    return data;
                });
        }
        return [];
    });
    const metersWithoutData = metersIdsWithoutData.map(c => {
        const entity = entities.find(con => con.meter?.meterId === c);
        return entity ? <any>{
            organisation: RefdataUtils.organisationInfoFormatter(entity.organisation.info),
            location: RefdataUtils.locationInfoFormatter(entity.location.info),
            buildingCode: entity.location.info.buildingCode,
            ean: entity.connection.info.code,
            marketSegment: entity.connection.info.marketSegment,
            connectionType: entity.connection.info.connectionType,
            measurementType: null,
            label: entity.meter?.info.label ?? null,
            meterType: entity.meter?.info.type ?? null,
            completeness: 0,
            ...Object.fromEntries(dynamicColumns.map(d => [d.fieldName, null]))
        } : null;
    });
    const chargePointData = [
        ...sortRecords(metersWithData.filter(r => r.connectionType === ConnectionType.ChargePoint)),
        ...metersWithoutData.filter(r => r.connectionType === ConnectionType.ChargePoint)
    ];
    const otherData = [
        ...sortRecords(metersWithData.filter(r => r.connectionType !== ConnectionType.ChargePoint)),
        ...metersWithoutData.filter(r => r.connectionType !== ConnectionType.ChargePoint)
    ];
    return { otherData: otherData, chargePointData: chargePointData };
}

function sortRecords(records: any[]) {
    return lodash.sortBy(records.filter(Boolean),
        [
            item => item.organisation?.toLowerCase(),
            item => item.location?.toLowerCase(),
            item => indexOfDataType(item.type),
            item => item.ean,
        ]);
}

function addDynamicColumns(sheet: XLSX.WorkSheet, template: SheetTemplate, dynamicColumns: DynamicColumn[], rowCount: number) {
    const key = template.dataKey || "data";
    dynamicColumns.forEach(d => template.template[key][d.fieldName] = new DynamicField(d.columnIndex, d.header));
    template.template[key] = new ArrayTemplate(template.template[key], [2, rowCount]);
    Object.values(template.template[key].pattern).filter(e => e["headerName"])
        .forEach((e: DynamicField) => sheet[e.field.replace("$", "1")] = <any>{v: e.headerName, t: "s"});
}

function getAllowedMeasurementTypes (addIntermediateData: boolean, addPower: boolean) {
    let measurements = [DataType.electricityConsumption, DataType.electricityConsumptionOffPeak,
        DataType.electricityFeedIn, DataType.electricityFeedInOffPeak, DataType.electricityGrossProduction, DataType.gasConsumption, DataType.waterConsumption,
        DataType.heatConsumption, DataType.chargePointConsumption];
    if (addIntermediateData) {
        measurements = measurements.concat(intermediaryDataTypes).filter(d => d != DataType.electricityGrossProduction)
    }
    if (addPower) measurements = measurements.concat(DataType.electricityPower, DataType.electricityFeedInPower, DataType.chargePointPower)
    return measurements
}

const intermediaryDataTypes = [DataType.electricityIntermediateConsumption, DataType.waterIntermediateConsumption, DataType.chargePointIntermediateConsumption]

function getDynamicColumns(template: WorkBookTemplate, monthSlots: Slot[], yearSlots: Slot[]) {
    const highestColumnField = (Object.values(template.sheets[0].template.data) as RequiredField[]).reduce(
        (a, b) =>
            excelColToNum(a.field.replace(/[^A-Z]/g, '')) > excelColToNum(b.field.replace(/[^A-Z]/g, ''))
                ? a : b);
    const columns: DynamicColumn[] = [];
    let currentHighestColumnName = highestColumnField.field.replace(/[^A-Z]/g, '');
    monthSlots.forEach(s => {
        currentHighestColumnName = getNextExcelColumnName(currentHighestColumnName);
        columns.push({
            fieldName: s.label,
            columnIndex: currentHighestColumnName + "$",
            header: s.label,
            slot: s
        });
    });
    yearSlots.forEach(s => {
        currentHighestColumnName = getNextExcelColumnName(currentHighestColumnName);
        columns.push({
            fieldName: s.label,
            columnIndex: currentHighestColumnName + "$",
            header: "Total " + s.label,
            slot: s
        });
    })
    return columns;
}

function excelColToNum(text: string): number {
    let num = 0;
    text = text.replace(/[^A-Z]/gi, '').toUpperCase();
    for (let i = 0; i < text.length; i++) {
        num = num * 26 + (text.charCodeAt(i) - 64);
    }
    return num;
}

function getNextExcelColumnName(columnName: string) {
    let result = columnName.split('');
    for (let i = result.length - 1; i >= 0; i--) {
        if (result[i] === 'Z') {
            result[i] = 'A';
            if (i === 0) {
                result.unshift('A');
                break;
            }
        } else {
            result[i] = String.fromCharCode(result[i].charCodeAt(0) + 1);
            break;
        }
    }
    return result.join('');
}

const dataTypesMapped = new Map<string, DataType[]>([
    [DataType.electricityConsumption, [DataType.electricityConsumption, DataType.electricityConsumptionOffPeak]],
    [DataType.electricityFeedIn, [DataType.electricityFeedIn, DataType.electricityFeedInOffPeak]]
]);


const bulkDataDownloadTemplate: WorkBookTemplate = {
    sheets: [
        {
            name: 'Data',
            template: {
                data: {
                    organisation: new RequiredField('A$'),
                    location: new RequiredField('B$'),
                    buildingCode: new RequiredField('C$'),
                    ean: new RequiredField('D$'),
                    surveyor: new RequiredField('E$'),
                    marketSegment: new RequiredField('F$'),
                    connectionType: new RequiredField('G$'),
                    measurementType: new RequiredField('H$'),
                    measurementUnit: new RequiredField("I$"),
                    completeness: new QuantityField("J$", 0, "0%"),
                    // Data columns are added when creating data
                }
            }
        }
    ]
};

const submetersDataDownloadTemplate: WorkBookTemplate = {
    sheets: [
        {
            name: 'Data',
            template: {
                data: {
                    organisation: new RequiredField('A$'),
                    location: new RequiredField('B$'),
                    buildingCode: new RequiredField('C$'),
                    ean: new RequiredField('D$'),
                    marketSegment: new RequiredField('E$'),
                    connectionType: new RequiredField('F$'),
                    measurementType: new RequiredField('G$'),
                    label: new RequiredField('H$'),
                    meterType: new RequiredField("I$"),
                    measurementUnit: new RequiredField("J$"),
                    completeness: new QuantityField("K$", 0, "0%"),
                }
            },
            dataKey: "data"
        },
        {
            name: 'ChargePoints',
            template: {
                chargePoints: {
                    organisation: new RequiredField('A$'),
                    location: new RequiredField('B$'),
                    buildingCode: new RequiredField('C$'),
                    ean: new RequiredField('D$'),
                    marketSegment: new RequiredField('E$'),
                    connectionType: new RequiredField('F$'),
                    measurementType: new RequiredField('G$'),
                    label: new RequiredField('H$'),
                    meterType: new RequiredField("I$"),
                    measurementUnit: new RequiredField("J$"),
                    completeness: new QuantityField("K$", 0, "0%"),
                }
            },
            dataKey: "chargePoints"
        }
    ]
};

const sortedDataTypes: DataType[] = [
    // Electricity
    DataType.electricityConsumption,
    DataType.electricityConsumptionOffPeak,
    DataType.electricityPower,
    DataType.electricityFeedIn,
    DataType.electricityFeedInOffPeak,
    DataType.electricityFeedInPower,
    DataType.electricityGrossProduction,
    DataType.electricityIntermediateConsumption,
    // Gas
    DataType.gasConsumption,
    // Heat
    DataType.heatConsumption,
    // water
    DataType.waterConsumption,
    DataType.waterIntermediateConsumption,
    // chargePoints
    DataType.chargePointConsumption,
    DataType.chargePointPower,
    DataType.chargePointIntermediateConsumption
];

const indexOfDataType = (measurementType?: DataType | null): number => {
    if (!measurementType) {
        return Number.MAX_SAFE_INTEGER;
    }
    const index = sortedDataTypes.indexOf(measurementType);
    return index === -1 ? Number.MAX_SAFE_INTEGER : (index + 1) * 100;
}

interface DynamicColumn {
    fieldName: string;
    columnIndex: string;
    header: string;
    slot: Slot;
}

interface BulkDownloadInfo {
    slots: Slot[];
    yearSlots: Slot[];
    dynamicColumns: DynamicColumn[];
    template: WorkBookTemplate;
}